import { useEffect } from 'react';
import Logger from '@medflex/utils/Logger';
import { User } from 'oidc-client';
import parseJwt from './parseJsonWebToken';

// When rendered within the WebView, Flutter injects a function "window.flutter_inappwebview.callHandler" that returns a promise
// that resolves with a new access token. Once the function is ready, an event "flutterInAppWebViewPlatformReady" is triggered
// If shouldUseWebViewHandler is true, the code below checks if the callHandler is available.
// If it is available, the callHandler is being called once initially and in the UserManager component once the token expires/is
// about to expire.
// The new user-session is being stored both in the UserManager class instance of the oidc-library and in the global state.

const useWebViewToken = ({ shouldUseWebViewHandler, manager, setSession }) => {
  const getToken = async () => {
    try {
      Logger.log('[UserManager::useWebViewToken::getToken]');
      // we use the value of the ref because otherwise the function might work with an old state value
      if (window.top.flutter_inappwebview && window.top.flutter_inappwebview.callHandler) {
        const token = await window.top.flutter_inappwebview.callHandler('token_required');

        if (!token) {
          Logger.warn(
            '[UserManager::useWebViewToken::getToken] - token received from the mobile app was undefined',
          );
          return null;
        }

        const jwt = parseJwt(token);
        if (!jwt) {
          Logger.error(`[UserManager::useWebViewToken::getToken]: Failed parsing jwt`);
          return null;
        }

        const { typ, userId, azp, exp } = jwt;

        const userData = {
          access_token: token,
          token_type: typ,
          expires_at: exp,
          expires_in: exp,
          expired: false,
          profile: { userId, azp },
        };
        // the UserManager class can only work with an instance of User of the oidc-client
        const user = new User(userData);
        if (user.expired) {
          Logger.warn('[UserManager::useWebViewToken::getToken] - WebView delivered expired token');
          return null;
        }
        manager.storeUser(user);
        setSession(user);
        // calling getUser resets the timer and we will get notified, when token expires/is about to expire
        return manager.getUser();
      }
      Logger.error(
        '[UserManager::useWebViewToken::getToken] - attempted to get token from WebView before call handler was ready',
      );
      return null;
    } catch (error) {
      Logger.error(`[UserManager::useWebViewToken::getToken]: ${error.message}`);
      return null;
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (shouldUseWebViewHandler && manager) {
      const handleWebViewReady = () => {
        Logger.log(
          '[UserManager::useWebViewToken::handleWebViewReady]: WebView callHandler is ready',
        );
        getToken();
      };
      // we check if the callHandler already exists. That way we ensure that we did not miss the event
      // because it has already been fired before the component rendered
      if (window.top.flutter_inappwebview && window.top.flutter_inappwebview.callHandler) {
        handleWebViewReady();
      } else {
        window.top.addEventListener('flutterInAppWebViewPlatformReady', handleWebViewReady);
        return () => {
          window.top.removeEventListener('flutterInAppWebViewPlatformReady', handleWebViewReady);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseWebViewHandler, manager]);

  return getToken;
};

export default useWebViewToken;
